import { useState } from 'react'
import { X, Copy, Download, CheckCircle, Settings, BookOpen } from 'lucide-react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'
import type { AIService } from '@/lib/supabase-client'
import { generateFieldShortcutCode } from '@/lib/code-template-generator'
import { DEFAULT_TEMPLATE_CONFIG, type TemplateConfig } from '@/types/template-config'
import FieldConfigEditor from './FieldConfigEditor'

interface CodeTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  service: AIService | null
  apiKey: string
}

export default function CodeTemplateModal({
  isOpen,
  onClose,
  service,
  apiKey,
}: CodeTemplateModalProps) {
  const [copied, setCopied] = useState(false)
  const [config, setConfig] = useState<TemplateConfig>(DEFAULT_TEMPLATE_CONFIG)
  const [activeTab, setActiveTab] = useState<'guide' | 'config'>('guide')

  if (!isOpen || !service) return null

  const code = generateFieldShortcutCode(service, apiKey, config)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
      alert('复制失败')
    }
  }

  const handleDownload = () => {
    const blob = new Blob([code], { type: 'text/typescript' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${service.name || service.service_id}-field-shortcut.ts`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const getTemplateType = () => {
    switch (service.provider) {
      case 'coze-workflow':
        return 'Coze Workflow (Execute → Query 轮询模式)'
      case 'coze-agent':
        return 'Coze Agent (Chat → Query 轮询模式)'
      default:
        return '通用同步调用模式'
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-7xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">飞书字段捷径代码模板</h2>
            <p className="text-sm text-gray-500 mt-1">{service.name || service.service_id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition"
            title="关闭"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Left/Right Layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Info & Config */}
          <div className="w-80 flex flex-col bg-gradient-to-br from-blue-50 to-indigo-50 border-r border-gray-200">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-white/50">
              <button
                onClick={() => setActiveTab('guide')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition ${
                  activeTab === 'guide'
                    ? 'text-blue-700 bg-white border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                使用说明
              </button>
              <button
                onClick={() => setActiveTab('config')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition ${
                  activeTab === 'config'
                    ? 'text-blue-700 bg-white border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Settings className="w-4 h-4" />
                字段配置
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {activeTab === 'guide' ? (
                <div className="space-y-6">
              {/* Service Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                  服务信息
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">服务类型:</span>
                    <div className="mt-1 px-3 py-2 bg-white rounded-lg font-medium text-gray-900">
                      {getTemplateType()}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">服务商:</span>
                    <div className="mt-1 px-3 py-2 bg-white rounded-lg font-medium text-gray-900">
                      {service.provider}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Service ID:</span>
                    <div className="mt-1 px-3 py-2 bg-white rounded-lg font-mono text-xs text-gray-700 break-all">
                      {service.service_id}
                    </div>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                  使用说明
                </h3>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="p-3 bg-white rounded-lg">
                    <div className="font-medium text-blue-700 mb-1">1. 复制或下载代码</div>
                    <div className="text-xs text-gray-600">
                      使用右上角按钮复制代码到剪贴板或下载为 .ts 文件
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <div className="font-medium text-blue-700 mb-1">2. 上传到飞书</div>
                    <div className="text-xs text-gray-600">
                      在飞书多维表格中创建字段捷径，上传此代码文件
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <div className="font-medium text-blue-700 mb-1">3. 配置字段</div>
                    <div className="text-xs text-gray-600">
                      按照捷径配置要求，映射输入和输出字段
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                  代码特性
                </h3>
                <div className="space-y-2 text-xs text-gray-700">
                  <div className="flex items-start gap-2 p-2 bg-white rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>已集成 APIMAkt 代理接口</span>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-white rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>自动轮询查询结果（60次重试）</span>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-white rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>完整的错误处理和日志输出</span>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-white rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>付费插件权限验证</span>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-white rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>域名白名单配置</span>
                  </div>
                </div>
              </div>

              {/* Warning */}
              {(service.provider === 'coze-workflow' || service.provider === 'coze-agent') && (
                <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                  <div className="text-xs font-semibold text-yellow-800 mb-1">注意事项</div>
                  <div className="text-xs text-yellow-700">
                    此模板使用异步执行 + 轮询查询模式，请确保您的 Coze 工作流支持异步调用。
                  </div>
                </div>
              )}

              {/* API Key Warning */}
              <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
                <div className="text-xs font-semibold text-red-800 mb-1">⚠️ 安全提醒</div>
                <div className="text-xs text-red-700">
                  代码中的 <code className="bg-red-100 px-1 rounded">API_KEY</code> 已留空。下载后请手动填写您的 API Key，切勿在公共场合分享包含真实 API Key 的代码。
                </div>
              </div>
                </div>
              ) : (
                <FieldConfigEditor config={config} onChange={setConfig} />
              )}
            </div>
          </div>

          {/* Right Panel - Code Display */}
          <div className="flex-1 flex flex-col overflow-hidden bg-gray-900">
            {/* Code Actions */}
            <div className="flex items-center justify-between px-6 py-3 bg-gray-800 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded">
                  TypeScript
                </span>
                <span className="text-xs text-gray-400">
                  {code.split('\n').length} 行
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      已复制
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      复制代码
                    </>
                  )}
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition"
                >
                  <Download className="w-4 h-4" />
                  下载文件
                </button>
              </div>
            </div>

            {/* Code Content */}
            <div className="flex-1 overflow-auto">
              <SyntaxHighlighter
                language="typescript"
                style={vscDarkPlus}
                showLineNumbers={true}
                customStyle={{
                  margin: 0,
                  padding: '1.5rem',
                  background: 'transparent',
                  fontSize: '0.875rem',
                }}
                lineNumberStyle={{
                  minWidth: '3.5em',
                  paddingRight: '1rem',
                  color: '#6b7280',
                  userSelect: 'none',
                }}
                wrapLongLines={false}
              >
                {code}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
